import { useState, useCallback, useEffect } from 'react'
import toast from 'react-hot-toast'
import {
  idpService,
  IndividualDevelopmentPlan,
  IDPItemStatus,
  PerformanceCategory,
  DevelopmentFocus,
  DevelopmentAreaType,
  InterventionType,
} from '@/services/api/idpService'
import type { DiscussionPoint } from '@/services/api/reviewService'
import type { RatingRationale, ReviewSynthesis } from '@/services/aiService'

interface UseIDPTransferParams {
  open: boolean
  memberId: string
  managerId?: string
  memberName: string
  overallRating: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr' | null
  reviewSessionId: string | null
  kpaDiscussionPoints: Record<string, { wentWell: DiscussionPoint[], challenges: DiscussionPoint[], actions: DiscussionPoint[] }>
  ratingRationale: Record<string, RatingRationale>
  reviewSynthesis: ReviewSynthesis | null
}

export function useIDPTransfer({
  open,
  memberId,
  managerId,
  memberName,
  overallRating,
  reviewSessionId,
  kpaDiscussionPoints,
  ratingRationale,
  reviewSynthesis,
}: UseIDPTransferParams) {
  const [memberIDP, setMemberIDP] = useState<IndividualDevelopmentPlan | null>(null)
  const [loadingIDP, setLoadingIDP] = useState(false)
  const [showIDPTransfer, setShowIDPTransfer] = useState(false)
  const [idpTransferItems, setIdpTransferItems] = useState<{
    strengths: { text: string; kpaId: string; selected: boolean }[]
    developmentAreas: { text: string; kpaId: string; selected: boolean }[]
    interventions: { text: string; kpaId: string; selected: boolean }[]
  }>({ strengths: [], developmentAreas: [], interventions: [] })
  const [transferringToIDP, setTransferringToIDP] = useState(false)

  useEffect(() => {
    const loadMemberIDP = async () => {
      if (!open || !memberId) return

      setLoadingIDP(true)
      try {
        const currentYear = new Date().getFullYear()
        if (import.meta.env.DEV) console.log('📋 Loading IDP for member:', memberId, 'year:', currentYear)
        const response = await idpService.getUserIDPs(memberId, currentYear)

        if (response.idps && response.idps.length > 0) {
          const currentIDP = response.idps[0]
          if (import.meta.env.DEV) console.log('📋 Found existing IDP:', currentIDP.id)
          setMemberIDP(currentIDP)
        } else {
          if (import.meta.env.DEV) console.log('📋 No IDP found for member - will initiate if needed')
          setMemberIDP(null)
        }
      } catch (error) {
        if (import.meta.env.DEV) console.log('ℹ️ Could not load IDP (may not exist yet):', error)
        setMemberIDP(null)
      } finally {
        setLoadingIDP(false)
      }
    }

    loadMemberIDP()
  }, [open, memberId])

  const mapRatingToPerformanceCategory = (rating: string): PerformanceCategory => {
    switch (rating) {
      case 'exceptional': return PerformanceCategory.EXCEPTIONAL
      case 'full': return PerformanceCategory.FULL
      case 'not-yet-full': return PerformanceCategory.NOT_YET_FULL
      case 'weak': return PerformanceCategory.WEAK
      default: return PerformanceCategory.FULL
    }
  }

  const handleInitiateIDP = useCallback(async () => {
    if (!memberId || !managerId) {
      toast.error('Missing member or manager information')
      return null
    }

    try {
      const currentYear = new Date().getFullYear()

      const newIDP = await idpService.createIDP(memberId, {
        user_id: memberId,
        performance_year: currentYear,
        performance_category: overallRating
          ? mapRatingToPerformanceCategory(overallRating)
          : PerformanceCategory.FULL,
        development_focus: DevelopmentFocus.IMPROVE_CURRENT,
        review_session_id: reviewSessionId || undefined,
        initiated_by_role: 'manager',
        initiated_by_user_id: managerId
      })

      setMemberIDP(newIDP)
      toast.success(`IDP initiated for ${memberName}`)
      return newIDP
    } catch (error) {
      console.error('Failed to initiate IDP:', error)
      toast.error('Failed to initiate IDP')
      return null
    }
  }, [memberId, managerId, memberName, overallRating, reviewSessionId])

  const prepareIDPTransferItems = useCallback(() => {
    const strengths: { text: string; kpaId: string; selected: boolean }[] = []
    const developmentAreas: { text: string; kpaId: string; selected: boolean }[] = []
    const interventions: { text: string; kpaId: string; selected: boolean }[] = []

    Object.entries(kpaDiscussionPoints).forEach(([kpaId, points]) => {
      if (points.wentWell) {
        points.wentWell.forEach(point => {
          if (point.content?.trim()) {
            strengths.push({ text: point.content, kpaId, selected: true })
          }
        })
      }

      if (points.challenges) {
        points.challenges.forEach(point => {
          if (point.content?.trim()) {
            developmentAreas.push({ text: point.content, kpaId, selected: true })
          }
        })
      }

      if (points.actions) {
        points.actions.forEach(point => {
          if (point.content?.trim()) {
            interventions.push({ text: point.content, kpaId, selected: true })
          }
        })
      }
    })

    Object.entries(ratingRationale).forEach(([kpaId, rationale]) => {
      if (rationale.strengthsSupporting) {
        rationale.strengthsSupporting.forEach(strength => {
          if (!strengths.some(s => s.text === strength)) {
            strengths.push({ text: strength, kpaId, selected: false })
          }
        })
      }
      if (rationale.gapsOrConcerns) {
        rationale.gapsOrConcerns.forEach(gap => {
          if (!developmentAreas.some(d => d.text === gap)) {
            developmentAreas.push({ text: gap, kpaId, selected: false })
          }
        })
      }
    })

    if (reviewSynthesis) {
      if (reviewSynthesis.keyAccomplishments) {
        reviewSynthesis.keyAccomplishments.forEach(achievement => {
          if (!strengths.some(s => s.text === achievement)) {
            strengths.push({ text: achievement, kpaId: '', selected: false })
          }
        })
      }
      if (reviewSynthesis.developmentPriorities) {
        reviewSynthesis.developmentPriorities.forEach(priority => {
          const priorityText = priority.area
          if (!developmentAreas.some(d => d.text === priorityText)) {
            developmentAreas.push({ text: priorityText, kpaId: '', selected: false })
          }
          priority.suggestedActions?.forEach(action => {
            if (!interventions.some(i => i.text === action)) {
              interventions.push({ text: action, kpaId: '', selected: false })
            }
          })
        })
      }
      if (reviewSynthesis.idpSuggestions) {
        reviewSynthesis.idpSuggestions.forEach(suggestion => {
          const interventionText = `${suggestion.focusArea}: ${suggestion.developmentGoal}`
          if (!interventions.some(i => i.text === interventionText)) {
            interventions.push({ text: interventionText, kpaId: '', selected: false })
          }
        })
      }
    }

    setIdpTransferItems({ strengths, developmentAreas, interventions })
    setShowIDPTransfer(true)
  }, [kpaDiscussionPoints, ratingRationale, reviewSynthesis])

  const toggleIDPTransferItem = useCallback((
    type: 'strengths' | 'developmentAreas' | 'interventions',
    index: number
  ) => {
    setIdpTransferItems(prev => ({
      ...prev,
      [type]: prev[type].map((item, i) =>
        i === index ? { ...item, selected: !item.selected } : item
      )
    }))
  }, [])

  const handleTransferToIDP = useCallback(async () => {
    let idp = memberIDP

    if (!idp) {
      idp = await handleInitiateIDP()
      if (!idp) return
    }

    setTransferringToIDP(true)

    try {
      const selectedStrengths = idpTransferItems.strengths.filter(s => s.selected)
      const selectedDevAreas = idpTransferItems.developmentAreas.filter(d => d.selected)
      const selectedInterventions = idpTransferItems.interventions.filter(i => i.selected)

      let successCount = 0

      for (const strength of selectedStrengths) {
        try {
          await idpService.addStrength(idp.id, {
            title: strength.text.substring(0, 100),
            description: strength.text,
            linked_kpa_ids: strength.kpaId ? [strength.kpaId] : undefined,
            item_status: IDPItemStatus.MANAGER_ADDED
          })
          successCount++
        } catch (error) {
          console.error('Failed to add strength:', error)
        }
      }

      for (const devArea of selectedDevAreas) {
        try {
          await idpService.addDevelopmentArea(idp.id, {
            title: devArea.text.substring(0, 100),
            description: devArea.text,
            area_type: DevelopmentAreaType.SKILLS,
            linked_kpa_ids: devArea.kpaId ? [devArea.kpaId] : undefined,
            priority: 2,
            item_status: IDPItemStatus.MANAGER_ADDED
          })
          successCount++
        } catch (error) {
          console.error('Failed to add development area:', error)
        }
      }

      for (const intervention of selectedInterventions) {
        try {
          await idpService.addIntervention(idp.id, {
            title: intervention.text.substring(0, 100),
            description: intervention.text,
            intervention_type: InterventionType.WORK_ASSIGNMENT,
            addresses_development_area_ids: undefined,
            item_status: IDPItemStatus.MANAGER_ADDED
          })
          successCount++
        } catch (error) {
          console.error('Failed to add intervention:', error)
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully transferred ${successCount} item(s) to IDP! 🎯`)

        const response = await idpService.getUserIDPs(memberId, new Date().getFullYear())
        if (response.idps && response.idps.length > 0) {
          setMemberIDP(response.idps[0])
        }
      }

      setShowIDPTransfer(false)
    } catch (error) {
      console.error('Failed to transfer to IDP:', error)
      toast.error('Failed to transfer items to IDP')
    } finally {
      setTransferringToIDP(false)
    }
  }, [memberIDP, memberId, idpTransferItems, handleInitiateIDP])

  return {
    memberIDP,
    loadingIDP,
    showIDPTransfer,
    setShowIDPTransfer,
    idpTransferItems,
    transferringToIDP,
    handleInitiateIDP,
    prepareIDPTransferItems,
    toggleIDPTransferItem,
    handleTransferToIDP,
  }
}
